/**
 * Start a family over with a new family key, keeping every number.
 *
 * The last way in, for when the family key is gone from every device: no
 * code, no unlocked phone, no old address that still has it. Server side and
 * the full account of what goes and what stays: services/api/valley/
 * startover.py. In one line -- every amount, date, balance, goal target and
 * allowance survives; every name, note and character is gone, and every
 * child's tablet and the wall display have to link again.
 *
 * Done from a LOCKED device, which is the point: everything here is new. A
 * fresh family key, a fresh person key for everybody (the old ones were
 * wrapped under the old family key), names the parent types again, and a new
 * family code to hold it all.
 */

import { api } from '../api/client';
import { sealable } from '../avatar/fragment';
import { createPersonKey, sealPersonKey } from '../crypto/content';
import { createFamilyKey, encryptField, packWrapped, wrapFamilyKey } from '../crypto/familyKey';
import { rememberKey } from '../crypto/vault';
import { normaliseFamilyCode, sealNameUnderCode, sealOwnEmail } from './families';
import { DEFAULT_UNIT, type Unit } from './money';
import { loadState, saveSettings, saveUnit } from './state';

/** Who is in the family, as the server can describe them without the key. */
export interface Unnamed {
  id: string;
  slot: number;
  /** In hundredths, the same as every amount. */
  held: number;
}

export interface WhatIsLeft {
  people: Unnamed[];
  /** Currency and marbles survive whole; a custom unit has lost its name. */
  unit: Unit | { type: 'custom'; whole: boolean };
}

/** Read what a start-over will keep: the people (by slot) and their money. */
export async function whatIsLeft(familyId: string): Promise<WhatIsLeft> {
  const raw = await api.get<{
    people: { id: string; slot: number; status?: string }[];
    held: Record<string, number>;
    unit?: { type?: string; currency?: string; whole?: boolean; step?: number };
  }>(`/v1/families/${familyId}/state`);
  const unit: WhatIsLeft['unit'] =
    raw.unit?.type === 'marbles'
      ? { type: 'marbles' }
      : raw.unit?.type === 'custom'
        ? { type: 'custom', whole: raw.unit.whole !== false }
        : raw.unit?.type === 'currency'
          ? { type: 'currency', currency: raw.unit.currency ?? 'USD' }
          : DEFAULT_UNIT;
  return {
    people: raw.people
      .filter((p) => p.status !== 'removed')
      .sort((a, b) => a.slot - b.slot)
      .map((p) => ({ id: p.id, slot: p.slot, held: raw.held[p.id] ?? 0 })),
    unit,
  };
}

export interface StartOverInput {
  familyName: string;
  /** Person id -> the name typed for them. */
  names: Record<string, string>;
  /** Only for a custom unit: what the family counts in, typed again. */
  unitNames?: { one: string; many: string; icon: string };
}

export async function startOver(
  familyId: string,
  input: StartOverInput,
): Promise<{ key: CryptoKey; code: string; emailed: number }> {
  const left = await whatIsLeft(familyId);
  const key = await createFamilyKey();

  const minted = await api.write<{ code: string }>('POST', `/v1/families/${familyId}/code`, {});
  const code = normaliseFamilyCode(minted.code);

  const people: Record<string, { pf: string; pfw: string }> = {};
  for (const person of left.people) {
    const personKey = await createPersonKey();
    people[person.id] = {
      pf: await encryptField(personKey, sealable({ name: (input.names[person.id] ?? '').trim() })),
      pfw: await sealPersonKey(key, personKey),
    };
  }

  const [keyWrap, codeCt, preview, emailCt] = await Promise.all([
    wrapFamilyKey(key, code).then(packWrapped),
    encryptField(key, code),
    sealNameUnderCode(input.familyName.trim(), code),
    sealOwnEmail(key),
  ]);

  // The server emails every other parent how to get back in -- never the
  // code (services/api/valley/notify.py) -- and says how many it reached.
  const done = await api.write<{ emailed?: number }>('POST', `/v1/families/${familyId}/start-over`, {
    code,
    keyWrap,
    codeCt,
    preview,
    people,
    ...(emailCt ? { emailCt } : {}),
  });
  await rememberKey(familyId, key);

  // The second half, through the ordinary routes: the family's name sealed
  // for the children too, and a custom unit's name. If either fails the
  // family is already started over and readable; the name can be set again
  // in Settings.
  try {
    const state = await loadState(familyId, key);
    await saveSettings(familyId, key, { name: input.familyName.trim() }, state.people);
    if (left.unit.type === 'custom' && input.unitNames) {
      await saveUnit(
        familyId,
        key,
        { type: 'custom', whole: left.unit.whole, ...input.unitNames },
        state.people,
      );
    }
  } catch {
    // See above.
  }
  return { key, code, emailed: done?.emailed ?? 0 };
}
